import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  FormControlLabel,
  Radio,
  RadioGroup,
  Typography,
} from "@mui/material";
import React, { useEffect, useState } from "react";

interface ExportFilterModalProps {
  open: boolean;
  onClose: () => void;
  onExport: (filter: ExportFilter) => void;
  isExporting: boolean;
  selectedCount?: number;
}

export type ExportFilter =
  | "all"
  | "candidates"
  | "rejected"
  | "approved"
  | "final"
  | "high_score"
  | "low_score"
  | "selection";

const ExportFilterModal: React.FC<ExportFilterModalProps> = ({
  open,
  onClose,
  onExport,
  isExporting,
  selectedCount = 0,
}) => {
  const [selectedFilter, setSelectedFilter] = useState<ExportFilter>(
    selectedCount > 0 ? "selection" : "all",
  );

  useEffect(() => {
    if (open) {
      setSelectedFilter(selectedCount > 0 ? "selection" : "all");
    }
  }, [open, selectedCount]);

  const handleExport = () => {
    onExport(selectedFilter);
  };

  const baseFilterOptions: {
    value: ExportFilter;
    label: string;
    description: string;
  }[] = [
    {
      value: "all",
      label: "All Entities",
      description: "Export all entities in the discovery list",
    },
    {
      value: "candidates",
      label: "Candidates",
      description: "Only entities with candidate status",
    },
    {
      value: "rejected",
      label: "Dismissed",
      description: "Only dismissed entities",
    },
    {
      value: "approved",
      label: "Evaluation",
      description: "Only entities moved to evaluation",
    },
    {
      value: "final",
      label: "Final Selection",
      description: "Only entities in final selection",
    },
    {
      value: "high_score",
      label: "High Score",
      description: "Entities with relevance score ≥ 50",
    },
    {
      value: "low_score",
      label: "Low Score",
      description: "Entities with relevance score < 50",
    },
  ];

  const filterOptions =
    selectedCount > 0
      ? [
          {
            value: "selection" as ExportFilter,
            label: `Selection (${selectedCount})`,
            description: `Only the ${selectedCount} currently selected ${selectedCount === 1 ? "entity" : "entities"}`,
          },
          ...baseFilterOptions,
        ]
      : baseFilterOptions;

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: { backgroundColor: "background.paper", backgroundImage: "none" },
      }}
    >
      <DialogTitle>Export Entities</DialogTitle>
      <DialogContent>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
          Select which entities you want to export to Excel
        </Typography>
        <FormControl component="fieldset" fullWidth>
          <RadioGroup
            value={selectedFilter}
            onChange={(e) => setSelectedFilter(e.target.value as ExportFilter)}
          >
            {filterOptions.map((option) => (
              <Box
                key={option.value}
                sx={{
                  mb: 0.5,
                  px: 1.5,
                  py: 0.5,
                  border: "1px solid",
                  borderColor:
                    selectedFilter === option.value
                      ? "primary.main"
                      : "divider",
                  borderRadius: 1,
                  backgroundColor:
                    selectedFilter === option.value
                      ? "action.selected"
                      : "transparent",
                  cursor: "pointer",
                  transition: "all 0.2s",
                  "&:hover": {
                    borderColor: "primary.main",
                    backgroundColor: "action.hover",
                  },
                }}
                onClick={() => setSelectedFilter(option.value)}
              >
                <FormControlLabel
                  value={option.value}
                  control={<Radio size="small" />}
                  label={
                    <Typography variant="body2">
                      <Box component="span" fontWeight={500}>
                        {option.label}
                      </Box>
                      {" — "}
                      <Box component="span" sx={{ color: "text.secondary" }}>
                        {option.description}
                      </Box>
                    </Typography>
                  }
                  sx={{ m: 0, width: "100%" }}
                />
              </Box>
            ))}
          </RadioGroup>
        </FormControl>
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button onClick={onClose} disabled={isExporting}>
          Cancel
        </Button>
        <Button
          onClick={handleExport}
          variant="contained"
          disabled={isExporting}
        >
          {isExporting ? "Exporting..." : "Export"}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default ExportFilterModal;
